import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack
} from '@mui/material';
import { usePreferences } from 'hooks/usePreferences';
import { formatDateWithPreferences } from 'utils/dateFormatUtils';

const DueEyeExamReport = ({ data, onPageChange }) => {
  const { preferences } = usePreferences();

  if (!data || !data.summary) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Due Eye Exam Report</Typography>
        <Typography color="textSecondary">No data available for this report.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Due Eye Exam Report
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Date range: {formatDateWithPreferences(data.dateFrom, preferences)} to {formatDateWithPreferences(data.dateTo, preferences)}
      </Typography>
      {data.location && (
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Location: {data.location}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card><CardContent><Typography color="textSecondary" gutterBottom>Total Due</Typography><Typography variant="h5">{data.summary.totalDueExams || 0}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card><CardContent><Typography color="textSecondary" gutterBottom>Overdue</Typography><Typography variant="h5" sx={{ color: 'error.main' }}>{data.summary.overdueCount || 0}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card><CardContent><Typography color="textSecondary" gutterBottom>Due Today</Typography><Typography variant="h5">{data.summary.dueTodayCount || 0}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card><CardContent><Typography color="textSecondary" gutterBottom>SMS Opt-In</Typography><Typography variant="h5">{data.summary.smsOptedInCount || 0}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card><CardContent><Typography color="textSecondary" gutterBottom>No Mobile</Typography><Typography variant="h5">{data.summary.noMobileCount || 0}</Typography></CardContent></Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>SMS Opt-In</TableCell>
              <TableCell>Exam Date</TableCell>
              <TableCell>Recall Date</TableCell>
              <TableCell align="right">Days Until Due</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.dueExams || []).map((exam) => (
              <TableRow key={exam.id}>
                <TableCell>{exam.customerName || 'N/A'}</TableCell>
                <TableCell>{exam.customerMobile || 'N/A'}</TableCell>
                <TableCell>{exam.customerEmail || 'N/A'}</TableCell>
                <TableCell>{exam.smsOptIn ? 'Yes' : 'No'}</TableCell>
                <TableCell>{formatDateWithPreferences(exam.examDate, preferences)}</TableCell>
                <TableCell>{formatDateWithPreferences(exam.recallDate, preferences)}</TableCell>
                <TableCell align="right" sx={{ color: (exam.daysUntilDue ?? 0) < 0 ? 'error.main' : 'text.primary' }}>{exam.daysUntilDue ?? 'N/A'}</TableCell>
                <TableCell>{exam.status || 'N/A'}</TableCell>
              </TableRow>
            ))}
            {(!data.dueExams || data.dueExams.length === 0) && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary">No due eye exams found for the selected filters</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {data.pagination && data.pagination.totalPages > 1 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Showing {data.pagination.startIndex} to {data.pagination.endIndex} of {data.pagination.totalCount} due exams
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Pagination
              count={data.pagination.totalPages}
              page={data.pagination.currentPage}
              onChange={(event, page) => onPageChange && onPageChange(page)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default DueEyeExamReport;
